// PuzzleGame.jsx
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { useEffect, useMemo, useState } from "react";
import PuzzlePic1 from "../assets/images/puzzles/puzzlePic1.jpg";
import PuzzlePic2 from "../assets/images/puzzles/puzzlePic2.jpg";
import PuzzlePic3 from "../assets/images/puzzles/puzzlePic3.jpg";
import PuzzlePic4 from "../assets/images/puzzles/puzzlePic4.jpg";
import PuzzlePic5 from "../assets/images/puzzles/puzzlePic5.jpg";
import Logo from "../assets/images/rabta logo.png";
import LostScreen from "./LostScreen";
import Tile from "./Tile";
import WinScreen from "./WinScreen";
import win from "../assets/images/win.png";
import Wallpaper1 from "../assets/images/wallpaper1.jpg";
import moment from "moment";

const MAX_TIME = 60;

// Shuffle the pieces ensuring no piece is in its original position
function shuffleOrder() {
  const arr = [0, 1, 2, 3];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  if (arr.every((v, i) => v === i)) return shuffleOrder();
  return arr;
}

export default function PuzzleGame({ }) {
  // Array of puzzle images
  const puzzleImages = [PuzzlePic1, PuzzlePic2, PuzzlePic3, PuzzlePic4, PuzzlePic5];

  // Select random puzzle image on component mount
  const [imgUrl] = useState(() => {
    const randomIndex = Math.floor(Math.random() * puzzleImages.length);
    return puzzleImages[randomIndex];
  });

  const [order, setOrder] = useState(() => shuffleOrder());
  const [activeId, setActiveId] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60); // 60 sec timer
  const [startTime] = useState(() => Date.now());
  const [gameResult, setGameResult] = useState(null); // 'win' or 'lost'
  const [showResultScreen, setShowResultScreen] = useState(false); // New state for delayed screen
  const [timeTaken, setTimeTaken] = useState(null); // Time taken to complete

  const [hasClaimableCashback, setHasClaimableCashback] = useState(false);
  const [claimedToday, setClaimedToday] = useState(false);
  const [instantCashbackLink, setinstantCashbackLink] = useState(null);
  const [cashbackLink, setCashbackLink] = useState(null);
  const [isCashbackLoading, setIsCashbackLoading] = useState(false);
  const [isGameSaving, setIsGameSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const solved = useMemo(() => order.every((v, i) => v === i), [order]);

  function onDragStart(e) {
    setActiveId(e.active.id);
  }

  function onDragEnd(e) {
    const { active, over } = e;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const a = parseInt(active.id, 10),
      b = parseInt(over.id, 10);
    setOrder((prev) => {
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
  }

  const handleStepTrack = async (stepName) => {
    try {
      // 1️⃣ Get consumer object from localStorage
      const consumer = JSON.parse(localStorage.getItem("consumer"));

      if (!consumer || !consumer._id) {
        console.error("❌ Consumer data or ID not found in localStorage");
        return;
      }

      const _id = consumer._id;

      // 2️⃣ Make PUT request to update step
      const response = await fetch(`${process.env.REACT_APP_API_URL}consumers/update/${_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          step: stepName,
        }),
      });

      const data = await response.json();

      // 3️⃣ Handle API response
      if (!response.ok) {
        console.error("❌ Failed to update step:", data.message || data);
        return;
      }

      console.log("✅ Step updated successfully:", data);
    } catch (error) {
      console.error("❌ Error while updating step:", error);
    }
  };

  // Function to send game result to API
  const sendGameResult = async (duration, status) => {
    handleStepTrack("Game Play Screen");
    setIsGameSaving(true);
    try {
      // Get consumer data from localStorage
      const consumerData = localStorage.getItem("consumer");
      if (!consumerData) {
        console.error("No consumer data found in localStorage");
        return;
      }

      const consumer = JSON.parse(consumerData);
      const consumerId = consumer._id;

      if (!consumerId) {
        setIsGameSaving(false);
        console.error("Consumer ID not found");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}consumers/game/${consumerId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          duration: duration.toFixed(4), // keep 4 decimal places (e.g. 8.5432)
          status: status,
        }),
      });

      if (!response.ok) {
        setIsGameSaving(false);
        throw new Error(`API call failed with status: ${response.status}`);
      }

      const data = await response.json();
      setinstantCashbackLink(data?.data?.cashgramLink);
      setIsGameSaving(false);
      //  console.log('Game result saved successfully:', data);
    } catch (error) {
      setIsGameSaving(false);
      //  console.error('Error saving game result:', error);
    } finally {
      setIsGameSaving(false);
    }
  };

  // Function to check cashback history
  const checkCashbackHistory = async () => {
    setIsCashbackLoading(true);
    try {
      // Get consumer data from localStorage
      const consumerData = localStorage.getItem("consumer");
      if (!consumerData) {
        console.error("No consumer data found in localStorage");
        setIsCashbackLoading(false);
        return;
      }

      const consumer = JSON.parse(consumerData);
      const mobileNumber = consumer.mobileNumber;

      if (!mobileNumber) {
        console.error("Mobile number not found");
        setIsCashbackLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}consumers/cashgram/history/${mobileNumber}`
      );

      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success" && result.data && Array.isArray(result.data)) {
        // Check if there's at least one entry with cashgramLink and status != CASHGRAM_REDEEMED
        const claimableEntry = result.data.find(
          (entry) => entry.cashgramLink && entry.cashgramStatus !== "CASHGRAM_REDEEMED"
        );

        const hasClaimedCashbackToday = result.data.find((entry) => {
          if (entry.cashgramLink && entry.cashgramStatus === "CASHGRAM_REDEEMED") {
            const updatedDate = moment(entry.updatedDate);
            const today = moment();

            return updatedDate.isSame(today, "day");
          }
          return false;
        });

        if (hasClaimedCashbackToday) {
          setClaimedToday(true);
        }

        if (claimableEntry) {
          setHasClaimableCashback(true);
          setCashbackLink(claimableEntry.cashgramLink);
          //   console.log('Claimable cashback found:', claimableEntry);
        } else {
          setHasClaimableCashback(false);
          setCashbackLink(null);
        }
      }
    } catch (error) {
      console.error("Error fetching cashback history:", error);
    } finally {
      setIsCashbackLoading(false);
    }
  };

  // Add 2-second delay before showing result screen
  useEffect(() => {
    if (gameResult) {
      const timer = setTimeout(() => {
        setShowResultScreen(true);
        handleStepTrack("Result Screen");
        checkCashbackHistory();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [gameResult]);


  // Timer effect
  useEffect(() => {
    if (gameResult) return;

    //  Time over → always lose
    if (timeLeft <= 0) {
      const timeUsed = MAX_TIME;
      setTimeTaken(timeUsed);
      setGameResult("lost");
      sendGameResult(timeUsed, "lost");
      return;
    }

    //  Solved within allowed time
    if (solved) {
      const timeUsed = Math.min((Date.now() - startTime) / 1000, 60);

      setTimeTaken(timeUsed);
      setGameResult("win");
      sendGameResult(timeUsed, "win");
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, solved, gameResult]);


  return (
    <div className="">
      <div className="page">
        <div className="wrap">
          <div className="controls">
            <img className="logo-img" src={Logo} alt="logo main" />
          </div>

          {showResultScreen ? (
            <>
              {gameResult === "win" ? (
                <div className="result-block">
                  <p>Thanks for participating</p>
                  <h2>PUZZLE SOLVED SUCCESSFULLY</h2>

                  {claimedToday ? (
                    <p>
                      Today's cash back is already claimed. Play again tomorrow for a chance to win
                      cash back and exciting prizes.
                    </p>
                  ) : hasClaimableCashback && cashbackLink ? (
                    <p>Don't forget, you have unclaimed cashback from your last game!</p>
                  ) : null}

                  <div className="cashback-section">
                    {isCashbackLoading || isGameSaving ? (
                      <div className="cashback-loader">
                        <div className="spinner"></div>
                        <p>Checking for cashback...</p>
                      </div>
                    ) : instantCashbackLink ? (
                      <a
                        className="claim-cashback claim-cashback-lg"
                        href={instantCashbackLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Claim Cashback
                      </a>
                    ) : hasClaimableCashback && cashbackLink ? (
                      <a
                        className="claim-cashback claim-cashback-lg"
                        href={"/consumer/cashbacks"}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Claim Cashback
                      </a>
                    ) : null}
                  </div>

                  <div className="poster">
                    <div className="overlay-text">
                      <p>A customised wallpaper (to the theme of Raabta)</p>
                      click{" "}
                      <a href={Wallpaper1} download="Raabta-Wallpaper.jpg">
                        here
                      </a>{" "}
                      to download
                    </div>
                  </div>

                  <img className="win-img" src={win} alt="logo-prize" />
                </div>
              ) : null}

              {gameResult === "lost" ? (
                <div className="result-block">
                  <p>Thanks for participating</p>
                  <h2>Prize Unlocked</h2>

                  {hasClaimableCashback && cashbackLink ? (
                    <p>Don't forget, you have unclaimed cashback from your last game!</p>
                  ) : (
                    <p>
                      Play & get a chance to win exciting <b>prizes and cashback.</b>
                    </p>
                  )}

                  <div className="cashback-section">
                    {isCashbackLoading ? (
                      <div className="cashback-loader">
                        <div className="spinner"></div>
                        <p>Checking for cashback...</p>
                      </div>
                    ) : hasClaimableCashback && cashbackLink ? (
                      <>
                        <a
                          className="claim-cashback claim-cashback-lg"
                          href={"/consumer/cashbacks"}
                          rel="noreferrer"
                        >
                          Claim Cashback
                        </a>
                      </>
                    ) : null}
                  </div>

                  <div className="poster">
                    <div className="overlay-text">
                      <p>A customised wallpaper (to the theme of Raabta)</p>
                      click{" "}
                      <a href={Wallpaper1} download="Raabta-Wallpaper.jpg">
                        here
                      </a>{" "}
                      to download
                    </div>
                  </div>

                  <img className="win-img" src={win} alt="logo-prize" />
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="timer-container">
                <div className="timer-badge">
                  <div className="timer-label">TIME</div>
                  <div className="timer-value">{timeLeft}</div>
                </div>
                <div className="timer-text">Solve the puzzle</div>
                <img src={imgUrl} alt="puzzle" className="puzzle-preview" />
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
              >
                <div className="board-outer">
                  <div className="board" style={{ touchAction: "none" }}>
                    {order?.map((pieceIndex, slotIndex) => (
                      <Tile
                        key={slotIndex}
                        id={String(slotIndex)}
                        imgUrl={imgUrl}
                        pieceIndex={pieceIndex}
                      />
                    ))}
                  </div>

                  {gameResult && (
                    <div
                      style={{
                        textAlign: "center",
                        marginTop: "20px",
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                    >
                      {gameResult === "win" ? "You made it!" : "You lost!"}
                    </div>
                  )}
                </div>
              </DndContext>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
